import { useMemo } from "react";
import { Link } from "react-router-dom";
import { cancelJob, jobStateLabel, useNow, type Job } from "../api/jobs";
import { useCourseIdMap, useCourses, useExtractModule, useRunChanges } from "../api/queries";
import { isTruncated, type ExtractionRun, type IndexRun, type MoodleDocument, type SyncEvent, type SyncRun } from "../api/types";
import { absoluteTime, decodeEntities, elapsed, titleCase } from "../lib/format";
import { Button, Empty, ErrorBanner, Loading, StatusBadge } from "./ui";

const ACTIONS = ["created", "updated", "deleted"] as const;

// --- sync ----------------------------------------------------------------------

export function SyncChanges({ run, compact = false }: { run: SyncRun; compact?: boolean }) {
  const changes = useRunChanges(run.id);
  const courses = useCourses();
  const courseName = useMemo(() => new Map((courses.data?.courses ?? []).map((c) => [c.moodle_id, c.fullname])), [courses.data]);

  const events = useMemo(() => changes.data?.events ?? [], [changes.data]);
  const byCourse = useMemo(() => {
    const groups = new Map<number | null, SyncEvent[]>();
    for (const e of events) {
      if (!groups.has(e.course_moodle_id)) groups.set(e.course_moodle_id, []);
      groups.get(e.course_moodle_id)!.push(e);
    }
    return [...groups.entries()];
  }, [events]);

  if (changes.error) return <ErrorBanner error={changes.error} />;
  if (changes.isLoading) return <Loading />;
  if (run.status === "failed") {
    return <p className="text-sm text-red-700">This sync failed and was rolled back, so nothing changed. {run.error}</p>;
  }
  if (!events.length) return <Empty>No changes. Moodle content was already up to date.</Empty>;
  const capped = changes.data?.capped && (
    <p className="mt-2 text-xs text-gray-500">Showing the first {events.length.toLocaleString()} changes of this run.</p>
  );

  if (compact) {
    return (
      <>
        <ul className="divide-y text-sm">
          {byCourse.map(([courseId, list]) => (
            <li key={courseId ?? "none"} className="flex items-center justify-between gap-4 py-2">
              {courseId !== null ? (
                <Link to={`/courses/${courseId}`} className="truncate hover:underline">{courseName.get(courseId) ?? `Course ${courseId}`}</Link>
              ) : <span>Not tied to a course</span>}
              <ActionCounts events={list} />
            </li>
          ))}
        </ul>
        {capped}
      </>
    );
  }

  return (
    <div className="space-y-5">
      {capped}
      {byCourse.map(([courseId, list]) => (
        <div key={courseId ?? "none"} className="overflow-hidden rounded-xl border border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            {courseId !== null ? (
              <Link to={`/courses/${courseId}`} className="font-medium text-gray-900 hover:underline">{courseName.get(courseId) ?? `Course ${courseId}`}</Link>
            ) : <span className="font-medium text-gray-900">Not tied to a course</span>}
            <ActionCounts events={list} />
          </div>
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className="bg-brand px-4 py-3 text-left text-xs font-semibold text-white">Action</th>
                <th className="bg-brand px-4 py-3 text-left text-xs font-semibold text-white">Type</th>
                <th className="bg-brand px-4 py-3 text-left text-xs font-semibold text-white">Name</th>
                <th className="bg-brand px-4 py-3 text-left text-xs font-semibold text-white">Moodle id</th>
              </tr>
            </thead>
            <tbody>
              {list.map((e) => (
                <tr key={e.id} className="bg-white">
                  <td className="border-t border-gray-100 px-4 py-3"><StatusBadge status={e.action} label={titleCase(e.action)} /></td>
                  <td className="border-t border-gray-100 px-4 py-3 text-gray-600">{e.entity_type}</td>
                  <td className="border-t border-gray-100 px-4 py-3">{eventName(e)}</td>
                  <td className="border-t border-gray-100 px-4 py-3 text-gray-500">{e.entity_moodle_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function ActionCounts({ events }: { events: SyncEvent[] }) {
  return (
    <span className="flex shrink-0 gap-1">
      {ACTIONS.map((a) => {
        const n = events.filter((e) => e.action === a).length;
        return n ? <StatusBadge key={a} status={a} label={`${n} ${titleCase(a)}`} /> : null;
      })}
    </span>
  );
}

function eventName(e: SyncEvent) {
  const p = e.payload ?? {};
  const name = p.fullname ?? p.name ?? p.filename ?? p.shortname;
  return typeof name === "string" ? decodeEntities(name) : <span className="text-gray-400">—</span>;
}

// Shown in place of run details while a job this browser started is still
// running. The API writes the run row, counts and change events in one
// transaction when the job ends; until then only the task state is known.
export function JobInProgress({ job, noun }: { job: Job; noun: string }) {
  const now = useNow(true);
  const state = jobStateLabel(job);
  return (
    <div className="rounded border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
      <div className="flex items-center gap-2 font-medium">
        <StatusBadge status={state === "queued" ? "queued" : "running"} label={state} /> {job.label}
      </div>
      <p className="mt-2">
        Started {absoluteTime(new Date(job.startedAt).toISOString())} · {elapsed(job.startedAt, now)} ago.
      </p>
      <p className="mt-1 text-blue-800">
        {state === "queued"
          ? "Waiting for a Celery worker to pick it up."
          : `Counts and details for this ${noun} appear here as soon as it finishes. This page checks every few seconds.`}
      </p>
      <button onClick={() => cancelJob(job.kind)} className="mt-2 text-xs text-blue-700 underline">
        Stop waiting (e.g. if the worker is down)
      </button>
    </div>
  );
}

// --- extraction ------------------------------------------------------------------

export function ExtractionOutput({ run, documents, compact = false }: {
  run: ExtractionRun;
  documents: MoodleDocument[] | undefined;
  compact?: boolean;
}) {
  const courseMap = useCourseIdMap();

  const byCourse = useMemo(() => {
    const groups = new Map<number, MoodleDocument[]>();
    for (const d of documents ?? []) {
      if (!groups.has(d.course_id)) groups.set(d.course_id, []);
      groups.get(d.course_id)!.push(d);
    }
    return [...groups.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [documents]);

  if (!documents) return <Loading />;

  const replaced = run.documents_written - documents.length;
  const summary = (
    <p className="mb-3 text-xs text-gray-500">
      {run.modules_seen} modules processed · {run.documents_written} documents written
      {replaced > 0 && <> · {documents.length} still current ({replaced} replaced by later re-extractions)</>}
      {run.failures > 0 && <span className="text-red-600"> · {run.failures} modules failed (listed below)</span>}
    </p>
  );

  const failures = !compact && run.failure_details.length > 0 && (
    <div className="mb-4"><ExtractionFailures run={run} /></div>
  );

  if (!documents.length) {
    return <>{summary}{failures}<Empty>{run.documents_written ? "All documents from this run have since been replaced." : "This run produced no documents."}</Empty></>;
  }

  return (
    <>
      {summary}
      {failures}
      <ul className={compact ? "divide-y text-sm" : "space-y-3 text-sm"}>
        {byCourse.map(([courseId, docs]) => {
          const course = courseMap.data?.get(courseId);
          const types = countBy(docs.map((d) => d.source_type));
          return (
            <li key={courseId} className={compact ? "py-2" : "rounded-xl border border-gray-200 px-4 py-3"}>
              <div className="flex items-center justify-between gap-4">
                {course ? (
                  <Link to={`/courses/${course.moodle_id}`} className="truncate font-medium text-gray-900 hover:underline">{course.fullname}</Link>
                ) : <span className="font-medium text-gray-500">Course #{courseId}</span>}
                <span className="flex shrink-0 items-center gap-3 text-xs text-gray-500">
                  {docs.length} docs
                  {course && <Link to={`/approvals?course=${course.moodle_id}`} className="font-medium text-brand hover:underline">Review</Link>}
                </span>
              </div>
              <div className={`flex flex-wrap gap-1 ${compact ? "mt-1" : "mt-2"}`}>
                {types.map(([type, n]) => (
                  <code key={type} className={compact ? "rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700" : "rounded bg-brand-light px-1.5 py-0.5 text-xs text-brand"}>{type} × {n}</code>
                ))}
              </div>
              {!compact && (
                <ul className="mt-2 space-y-0.5 pl-3 text-xs text-gray-600">
                  {docs.slice(0, 50).map((d) => (
                    <li key={d.id} className="truncate">{d.title || "Untitled"} <span className="text-gray-400">· {d.source_path} · {d.char_count.toLocaleString()} ch</span></li>
                  ))}
                  {docs.length > 50 && <li className="text-gray-400">…and {docs.length - 50} more</li>}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

function countBy(values: string[]) {
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}

// --- failures ------------------------------------------------------------------

function TruncatedNote({ n }: { n: number }) {
  return <p className="mt-2 text-xs text-gray-500">…and {n.toLocaleString()} more failures not listed.</p>;
}

// failure_details is [] on runs recorded before the API tracked it.
function NoDetails({ failures }: { failures: number }) {
  return <p className="text-xs text-gray-500">{failures} failed, but this run predates per-item failure details.</p>;
}

export function ExtractionFailures({ run }: { run: ExtractionRun }) {
  const retry = useExtractModule();
  const entries = run.failure_details.filter((e) => !isTruncated(e));
  const truncated = run.failure_details.find(isTruncated);
  if (!run.failure_details.length) return run.failures ? <NoDetails failures={run.failures} /> : null;
  return (
    <div>
      <h3 className="mb-1 text-sm font-medium text-red-700">Failed modules</h3>
      <ErrorBanner error={retry.error} />
      {retry.data && <p className="mb-2 text-xs text-green-700">Module re-extracted: {retry.data.documents_written} documents written ({retry.data.status}).</p>}
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className="bg-brand px-4 py-3 text-left text-xs font-semibold text-white">cmid</th>
              <th className="bg-brand px-4 py-3 text-left text-xs font-semibold text-white">Module</th>
              <th className="bg-brand px-4 py-3 text-left text-xs font-semibold text-white">Error</th>
              <th className="bg-brand px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {entries.map((f) => !isTruncated(f) && (
              <tr key={f.cmid} className="bg-white">
                <td className="border-t border-gray-100 px-4 py-3 text-gray-500">{f.cmid}</td>
                <td className="border-t border-gray-100 px-4 py-3">{decodeEntities(f.module_name) ?? <span className="text-gray-400">—</span>}</td>
                <td className="max-w-lg break-words border-t border-gray-100 px-4 py-3 text-xs text-red-700">{f.error}</td>
                <td className="border-t border-gray-100 px-4 py-3 text-right">
                  <Button loading={retry.isPending && retry.variables === f.cmid} disabled={retry.isPending} onClick={() => retry.mutate(f.cmid)}>Retry</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {truncated && <TruncatedNote n={truncated.truncated} />}
    </div>
  );
}

export function IndexFailures({ run }: { run: IndexRun }) {
  const entries = run.failure_details.filter((e) => !isTruncated(e));
  const truncated = run.failure_details.find(isTruncated);
  if (!run.failure_details.length) return run.failures ? <NoDetails failures={run.failures} /> : <Empty>No failures.</Empty>;
  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className="bg-brand px-4 py-3 text-left text-xs font-semibold text-white">Document</th>
              <th className="bg-brand px-4 py-3 text-left text-xs font-semibold text-white">Stage</th>
              <th className="bg-brand px-4 py-3 text-left text-xs font-semibold text-white">Error</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((f, i) => !isTruncated(f) && (
              <tr key={`${f.document_id}-${i}`} className="bg-white">
                <td className="border-t border-gray-100 px-4 py-3 text-gray-500">#{f.document_id}</td>
                <td className="border-t border-gray-100 px-4 py-3"><code className="text-xs">{f.stage}</code></td>
                <td className="max-w-lg break-words border-t border-gray-100 px-4 py-3 text-xs text-red-700">{f.error}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {truncated && <TruncatedNote n={truncated.truncated} />}
    </div>
  );
}
